/* eslint-disable */
import {
  extract_items,
  first_value,
  parse_response_data,
  title_case
} from '@/services/onboarding_data_helpers';

export const ONBOARDING_JOURNEY_KEYS = ['supplier', 'employee', 'merchant', 'partner', 'intern', 'client'];
export const JOURNEY_LABELS = new Map(ONBOARDING_JOURNEY_KEYS.map((journey_key) => [journey_key, title_case(journey_key)]));
export const ONBOARDING_EXCLUDED_PROJECT_NAMES = ['proba global'];

export const onboarding_request_cache = {
  all_cases: new Map(),
  assigned_cases: new Map(),
  application_forms: new Map(),
  application_form: new Map(),
  application_profile_completion: new Map(),
  application_profile_completion_summary: new Map(),
  application_documents: new Map(),
  application_transition_history: new Map(),
  entity_profiles: new Map(),
  entity_type_questions: new Map(),
  entity_types: new Map(),
  forms_for_entity_type: new Map(),
  entity_type_verification_bindings: new Map(),
  verification_services: new Map(),
  full_config_by_entity_type: new Map(),
  manual_verification_history: new Map(),
  manual_verification_status: new Map(),
  onboarding_status: new Map(),
  workflow_details: new Map(),
  workflow_statuses: new Map(),
  entity_type_workflows: new Map()
};

export async function map_with_concurrency_limit(items = [], mapper = async (value) => value, concurrency = 6) {
  const source_items = Array.isArray(items) ? items : [];
  if (!source_items.length) return [];

  const limit = Math.max(1, Number(concurrency || 1));
  const results = new Array(source_items.length);
  let next_index = 0;

  const run_next = async () => {
    while (next_index < source_items.length) {
      const current_index = next_index;
      next_index += 1;
      results[current_index] = await mapper(source_items[current_index], current_index);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(limit, source_items.length) }, () => run_next())
  );

  return results;
}

export const get_entity_type_id = (entity_type) => first_value([
  entity_type?.entity_type_id,
  entity_type?.entity_type?.id,
  entity_type?.current_entity_type?.id,
  entity_type?.id
], null);

export const get_entity_type_name = (entity_type) => first_value([
  entity_type?.entity_type?.entity_type,
  entity_type?.entity_type?.entity_type_name,
  entity_type?.entity_type_name,
  entity_type?.entity_type,
  entity_type?.name,
  entity_type?.title
], '');

export function get_dynamic_journey_key(entity_type = {}) {
  const entity_type_id = get_entity_type_id(entity_type);
  return entity_type_id ? String(entity_type_id) : get_journey_key_from_name(entity_type);
}

function normalize_journey_source_name(source_name) {
  return String(source_name || 'onboarding')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function get_journey_key_from_name(entity_type = {}) {
  return normalize_journey_source_name(get_journey_source_name(entity_type)) || 'onboarding';
}

function get_journey_source_name(entity_type = {}) {
  return get_entity_type_name(entity_type) || entity_type?.journey_key || entity_type?.name;
}

export function normalize_onboarding_entity_type(entity_type = {}) {
  const id = get_entity_type_id(entity_type);
  const name = get_entity_type_name(entity_type) || 'Entity type';
  const journey_key = get_dynamic_journey_key(entity_type);

  return {
    ...entity_type,
    id,
    name,
    source_name: name,
    journey_key,
    entity_type_id: id
  };
}

export const extract_entity_type_forms = (response) => {
  const parsed = parse_response_data(response);
  const candidates = [
    parsed?.data?.assigned_forms,
    parsed?.data?.workflow_assigned_forms,
    parsed?.assigned_forms,
    parsed?.workflow_assigned_forms,
    extract_items(response)
  ];

  return candidates.find((candidate) => Array.isArray(candidate)) || [];
};

export const extract_config_form_bindings = (response) => {
  const parsed = parse_response_data(response);
  const candidates = [
    parsed?.data?.form_bindings,
    parsed?.form_bindings
  ];

  return candidates.find((candidate) => Array.isArray(candidate)) || [];
};

export const extract_config_workflow_bindings = (response) => {
  const parsed = parse_response_data(response);
  const candidates = [
    parsed?.data?.workflow_bindings,
    parsed?.workflow_bindings
  ];

  return candidates.find((candidate) => Array.isArray(candidate)) || [];
};

export const get_workflow_binding_id = (binding) => first_value([
  binding?.entity_type_workflow_id,
  binding?.entity_type_workflow,
  binding?.workflow_id,
  binding?.workflow?.id,
  binding?.id
], null);

export const normalize_form_binding = (binding) => ({
  ...binding,
  id: first_value([
    binding?.form,
    binding?.form_id,
    binding?.form?.id,
    binding?.id
  ], null),
  name: first_value([
    binding?.form_name,
    binding?.form?.form_name,
    binding?.form?.name,
    binding?.name,
    binding?.title
  ], 'Form')
});

export const dedupe_cases = (items) => {
  const seen_ids = new Set();

  return items.filter((case_item) => {
    const key_parts = [
      case_item.application_id || '',
      case_item.entity_profile_id || '',
      case_item.id || ''
    ].map((value) => String(value));
    const key = key_parts.join('-');
    if (seen_ids.has(key)) return false;

    seen_ids.add(key);
    return true;
  });
};

export const read_local_onboarding_case_metadata = () => {
  if (typeof window === 'undefined') return [];

  try {
    const stored = sessionStorage.getItem('onboarding_case_metadata');
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const extract_entity_type_config_payload = (response) => {
  const parsed = parse_response_data(response);
  return first_value([parsed?.data, parsed], {});
};

function get_case_tone(case_item) {
  return String(case_item?.journey_tone || case_item?.entity_type_id || '').trim();
}

function build_filter_count_map(cases) {
  return cases.reduce((counts, case_item) => {
    const tone = get_case_tone(case_item);
    if (!tone) return counts;

    const next_count = Number(counts.get(tone) || 0) + 1;
    counts.set(tone, next_count);
    return counts;
  }, new Map());
}

function has_filter_for_tone(filters, tone) {
  return filters.some((filter) => filter.tone === tone);
}

export function build_journey_filters(cases) {
  const filter_counts = build_filter_count_map(cases);
  const filters = [{ label: 'All', count: String(cases.length), tone: 'all' }];

  cases.forEach((case_item) => {
    const tone = get_case_tone(case_item);
    if (!tone || has_filter_for_tone(filters, tone)) return;

    filters.push({
      label: case_item?.journey || title_case(tone),
      count: String(filter_counts.get(tone) || 0),
      tone
    });
  });

  return filters;
}
